import { Button, createStyles, fade, List, Theme, Typography, withStyles } from "@material-ui/core";
import { inject, observer } from "mobx-react";
import React from "react";
import { PredefinedStoreName } from "../enums/PredefinedStoreName";
import { IContextStoreProps } from "../../stores/IContextStoreProps";
import { IConfigStoreProps } from "../../stores/IConfigStoreProps";
import Snapshot from "./Snapshot";
import meetingSnapshotDataService from "../../services/api/MeetingSnapshotDataService";
import { INotificationStoreProps } from "../../stores/INotificationStoreProps";

import Sound from 'react-sound'
import { MeetingSnapshot } from "../../domain/api/meetings/MeetingSnapshot";
import i18n from "../../services/localization/i18n";
import { observe } from "mobx";
import { KeyDownMessage } from "meet-addons-sdk";
import addonMessanger from "../workspace/AddonMessanger";
import AppIcon from "../shared/AppIcon";
import { AppIconType } from "../shared/AppIconType";
import SnapshotCard from "./SnapshotCard";

const styles = (theme: Theme) => createStyles({
    button: {
        color: theme.palette.text.primary,
        marginBottom: theme.spacing(),
        marginTop: theme.spacing(),
    },
    empty: {
        alignItems: "center",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        height: 200
    },
    goLive: {
        color: theme.palette.text.primary,
    },
    root: {
        "&:hover": {
            backgroundColor: theme.palette.background.default,
        },
        "backgroundColor": fade(theme.palette.background.default, 0.9),
        borderColor: theme.palette.secondary.main,
        borderStyle: "solid",
        borderWidth: "1",
        width: theme.spacing(50),
        position: "absolute",
        top: theme.spacing(13),
        right: theme.spacing(),
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        padding: theme.spacing()
    },
});

interface ISnapshotsProps {
    classes: {
        button: string;
        empty: string;
        goLive: string;
        root: string;
    }

    autoSnap: boolean;
    onClose: () => void;
}

interface ISnapshotState {
    playSound: boolean;
    selectedSnapshot: MeetingSnapshot | null;
}

@inject(PredefinedStoreName.CONTEXT)
@inject(PredefinedStoreName.CONFIG)
@inject(PredefinedStoreName.NOTIFICATION)
@observer
class Snapshots extends React.Component<ISnapshotsProps & IContextStoreProps & INotificationStoreProps & IConfigStoreProps, ISnapshotState> {
    
    private mounted: boolean = false;

    /**
     * Creates an instance of Snapshots.
     * @param {(ISnapshotsProps & IContextStoreProps & INotificationStoreProps & IConfigStoreProps)} props
     * @memberof Snapshots
     */
    constructor(props: ISnapshotsProps & IContextStoreProps & INotificationStoreProps & IConfigStoreProps) {
        super(props);
        
        (window as any).soundManager.setup({ 
            debugMode: false,
            preferFlash: false,
            useConsole: false,
        });

        this.state = {
            playSound: false,
            selectedSnapshot: null
        }

        if (props.autoSnap) {
            this.handleSnapshotRequest();
        }

        observe(this.props.contextStore!, "keyDownMessage", (change) => this.handleKeyDown(change.newValue!));
    }

    public componentDidMount() {
        this.mounted = true;
    }

    public componentWillUnmount() {
        this.mounted = false;
    }

    public render() {
        const { loadedSnapshot }  = this.props.contextStore!;
        
        if (!loadedSnapshot) {
            return this.renderSnapshotList();
        } else {
            return <SnapshotCard snapshot={loadedSnapshot} 
                              onGoLive={this.goLive} />;
        }
    }

    public renderSnapshotList() {

        const { classes }  = this.props;
        const { snapshots }  = this.props.contextStore!;
        const { hosts }  = this.props.configStore!;

        const { playSound }  = this.state;

        return <div className={classes.root} onKeyDown={this.handleKeyDown}>

            { this.renderCta() }
            
            { snapshots.length > 0 &&  this.renderSnapshots(snapshots) } 

            { snapshots.length === 0 && this.noSnapshots() } 

            <Sound url={`${hosts.cdn}/assets/audio/shutter2.mp3`} 
                    playStatus={playSound ? 'PLAYING' : 'STOPPED'} 
                    autoLoad={true}
                    onFinishedPlaying={() => this.setState({
                        playSound: false
                    })}
            />
        </div>;
    }

    private renderCta = () => {
        const { classes }  = this.props;
        const { finishedMeet }  = this.props.contextStore!;

        if (!finishedMeet) {
            return <Button variant="contained" 
                            color="secondary" 
                            className={classes.button}
                            onClick={this.handleSnapshotRequest}>
                <AppIcon icon={AppIconType.Snapshot} style={{ marginRight: 8}} />
                { i18n.t("snapshots_cta_text") }
            </Button>

        }

        return;
    }

    private renderSnapshots = (snapshots: MeetingSnapshot[]) => {

        const { selectedSnapshot } = this.state;

        const distinctUserIds = snapshots.map(p => p.userId).filter((x, i, a) => a.indexOf(x) === i);

        return <div>
            <Typography variant="h6" color="textPrimary" style={{
                marginLeft: 16,
                marginTop: 8
            }}>
                Recorded snapshots 
            </Typography>
            <List component="nav" aria-label="snapshots">
                { 
                    snapshots.map((sn, index) =>  {
                        return <Snapshot key={`snapshot-${index}`} 
                                        snapshot={sn} 
                                        showAvatar={distinctUserIds.length > 1} 
                                        selected={selectedSnapshot?.id === sn?.id}
                                        onSnapshotSelect={this.onSelect}
                                        onSnapshotDelete={this.onDeleteAsync}
                                        onSnapshotLoad={this.onLoadAsync}
                        />
                    }) 
                }
            </List>
        </div>
    }


    private onSelect = (snapshot: MeetingSnapshot) => {
        this.setState({
            selectedSnapshot: snapshot
        })
    }

    private goLive = async () => {
        const { setLoadedSnapshot }  = this.props.contextStore!;
        
        addonMessanger.sendSnapshotLoadMessageToAddons("live");
        setLoadedSnapshot();
    }

    private onLoadAsync = async (snapshot: MeetingSnapshot) => {
        const { setLoadedSnapshot }  = this.props.contextStore!;
        
        addonMessanger.sendSnapshotLoadMessageToAddons(snapshot.id!)
        setLoadedSnapshot(snapshot);
    }

    private onDeleteAsync = async (snapshot: MeetingSnapshot) => {

        const { removeSnapshot }  = this.props.contextStore!;

        this.setState({
            selectedSnapshot: null
        })

        await meetingSnapshotDataService.deleteSnapshotsAsync(snapshot.id!);

        removeSnapshot(snapshot);
        
    }

    private handleSnapshotRequest = () => {
        
        const { addOrUpdateSnapshot, addSnapshotUser, finishedMeet } = this.props.contextStore!;
        const { showToast } = this.props.notificationStore!;
        

        if (finishedMeet) {
            return;   
        }

        if (this.mounted) {
            this.setState({
                playSound: true,
            });
        }

        meetingSnapshotDataService.takeSnapshotAsync()
            .then(snapshotInfo => {
                snapshotInfo.users.forEach(u => addSnapshotUser(u));

                showToast(i18n.t("snapshot_toast_text"), 'success', 3000);

                this.setState({
                    selectedSnapshot: snapshotInfo,
                }, () => {
                    addOrUpdateSnapshot(snapshotInfo);
                });
            })
    }
    
    private noSnapshots = () => {
        const { classes }  = this.props;
        
        return <div className={classes.empty}>
            <Typography variant="overline" color="textPrimary">
                { i18n.t("snapshots_none_text") }
            </Typography>;
        </div>;
    }
    
    private handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement> | KeyDownMessage) => {
        switch (e.keyCode) {
            case 27: // esc
                this.props.onClose();
                break;
        }
    }

}

export default withStyles(styles)(Snapshots);
